/**
 * NexusReferralQueue - Filterable referral queue with status badges, urgency, SLA
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Search, RefreshCw, ChevronRight, Clock, AlertTriangle,
  CheckCircle2, XCircle, ArrowUpRight, Loader2, Filter,
  Zap, Play, Route, FileText, StickyNote
} from "lucide-react";
import NexusReferralDetail from './NexusReferralDetail';

const STATUS_CONFIG = {
  intake: { label: "Intake", color: "bg-blue-100 text-blue-700", icon: FileText },
  triaged: { label: "Triaged", color: "bg-amber-100 text-amber-700", icon: Filter },
  matching: { label: "Matching", color: "bg-purple-100 text-purple-700", icon: Zap },
  routed: { label: "Routed", color: "bg-cyan-100 text-cyan-700", icon: Route },
  accepted: { label: "Accepted", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  declined: { label: "Declined", color: "bg-red-100 text-red-700", icon: XCircle },
  redirected: { label: "Redirected", color: "bg-orange-100 text-orange-700", icon: ArrowUpRight },
  placed: { label: "Placed", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-slate-100 text-slate-600", icon: XCircle },
  stale: { label: "Stale", color: "bg-red-50 text-red-600", icon: AlertTriangle },
};

const URGENCY_CONFIG = {
  routine: { label: "Routine", color: "text-slate-600 bg-slate-50 border-slate-200" },
  urgent: { label: "Urgent", color: "text-amber-700 bg-amber-50 border-amber-200" },
  emergent: { label: "Emergent", color: "text-red-700 bg-red-50 border-red-200" },
};

const NexusReferralQueue = ({ token, API, user, onSelectReferral, selectedReferral }) => {
  const [referrals, setReferrals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", urgency: "", search: "" });
  const [detailReferral, setDetailReferral] = useState(null);

  const fetchReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.urgency) params.append("urgency", filters.urgency);
      if (filters.search) params.append("search", filters.search);
      params.append("limit", "50");

      const res = await fetch(`${API}/api/nexus/referrals?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setReferrals(data.data || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, [API, token, filters]);

  useEffect(() => { fetchReferrals(); }, [fetchReferrals]);

  const getSlaStatus = (ref) => {
    if (!ref.created_at || !ref.sla_target_hours) return null;
    const created = new Date(ref.created_at);
    const now = new Date();
    const elapsed = (now - created) / (1000 * 60 * 60);
    const pct = (elapsed / ref.sla_target_hours) * 100;
    if (pct >= 100) return { label: "SLA Breached", color: "text-red-600" };
    if (pct >= 75) return { label: "SLA Warning", color: "text-amber-600" };
    return { label: `${Math.round(ref.sla_target_hours - elapsed)}h left`, color: "text-slate-500" };
  };

  if (detailReferral) {
    return (
      <NexusReferralDetail
        referralId={detailReferral}
        token={token}
        API={API}
        user={user}
        onBack={() => { setDetailReferral(null); fetchReferrals(); }}
      />
    );
  }

  return (
    <div data-testid="nexus-referral-queue" className="space-y-4">
      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="py-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input
                data-testid="queue-search"
                placeholder="Search by name or need..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && fetchReferrals()}
              />
            </div>
            <div className="w-40">
              <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="queue-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Select value={filters.urgency} onValueChange={(v) => setFilters(prev => ({ ...prev, urgency: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="queue-urgency-filter"><SelectValue placeholder="All Urgency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Urgency</SelectItem>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="emergent">Emergent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={fetchReferrals} data-testid="queue-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Queue Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{total} referral{total !== 1 ? "s" : ""}</p>
      </div>

      {/* Referral List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      ) : referrals.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center text-slate-500">
            <Zap className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No referrals found</p>
            <p className="text-sm mt-1">Create a new referral from the &quot;New Referral&quot; tab</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {referrals.map((ref) => {
            const statusCfg = STATUS_CONFIG[ref.status] || STATUS_CONFIG.intake;
            const urgCfg = URGENCY_CONFIG[ref.urgency] || URGENCY_CONFIG.routine;
            const sla = getSlaStatus(ref);
            const StatusIcon = statusCfg.icon;

            return (
              <Card
                key={ref.referral_id}
                data-testid={`referral-card-${ref.referral_id}`}
                className="border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => setDetailReferral(ref.referral_id)}
              >
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-4">
                    {/* Status Icon */}
                    <div className={`p-2 rounded-lg ${statusCfg.color}`}>
                      <StatusIcon className="w-4 h-4" />
                    </div>

                    {/* Main Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-900 truncate">
                          {ref.client_first_name} {ref.client_last_name}
                        </span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusCfg.color} border-0`}>
                          {statusCfg.label}
                        </Badge>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border ${urgCfg.color}`}>
                          {urgCfg.label}
                        </Badge>
                        {ref.risk_level === "high" || ref.risk_level === "critical" ? (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border border-red-300 text-red-700 bg-red-50">
                            {ref.risk_level === "critical" ? "CRITICAL" : "HIGH RISK"}
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {ref.service_requested || "—"} &middot; {ref.client_county || "—"} &middot; {ref.presenting_need?.slice(0, 60) || "—"}
                      </p>
                    </div>

                    {/* SLA & Date */}
                    <div className="text-right shrink-0">
                      {sla && (
                        <div className={`text-[10px] font-medium ${sla.color} flex items-center gap-1 justify-end`}>
                          <Clock className="w-3 h-3" />
                          {sla.label}
                        </div>
                      )}
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {ref.created_at ? new Date(ref.created_at).toLocaleDateString() : ""}
                      </p>
                    </div>

                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NexusReferralQueue;
